import { useState, useRef, useEffect } from "react";
import {
  IoSend,
  IoArrowBack,
  IoSparkles,
  IoTime,
  IoCalendar,
  IoCall,
} from "react-icons/io5";
import { RiRobot2Fill, RiUser3Fill } from "react-icons/ri";

// ============================================
// CONFIGURATION
// ============================================
const MESSAGE_LIMIT = 5;
const API_ENDPOINT = "https://api.dilanaslan.com/api/asistan/chat";

// ============================================
// LOCAL STORAGE HELPERS
// ============================================
const getMessageData = () => {
  try {
    const data = localStorage.getItem("dilanAslanChatLimit");
    if (!data) return { count: 0, date: new Date().toDateString() };

    const parsed = JSON.parse(data);

    if (parsed.date !== new Date().toDateString()) {
      return { count: 0, date: new Date().toDateString() };
    }

    return parsed;
  } catch {
    return { count: 0, date: new Date().toDateString() };
  }
};

const incrementMessageCount = () => {
  const current = getMessageData();
  const newData = {
    count: current.count + 1,
    date: new Date().toDateString(),
  };
  localStorage.setItem("dilanAslanChatLimit", JSON.stringify(newData));
  return newData.count;
};

const canSendMessage = () => {
  return getMessageData().count < MESSAGE_LIMIT;
};

// ============================================
// QUICK QUESTIONS
// ============================================
const quickQuestions = [
  { text: "Çocuğum geç konuşuyor", icon: "🗣️" },
  { text: "Davranış problemleri", icon: "😤" },
  { text: "Uyku düzeni bozuk", icon: "🌙" },
  { text: "Oyun oynamıyor", icon: "🧸" },
  { text: "Yemek seçiciliği", icon: "🥗" },
  { text: "Randevu almak istiyorum", icon: "📅" },
];

// ============================================
// MAIN COMPONENT
// ============================================
export default function AIChatPage() {
  const [messages, setMessages] = useState([
    {
      type: "bot",
      text: "Merhaba! 👋 Ben Dilan Aslan Çocuk Gelişim Danışmanlığı'nın dijital asistanıyım. Çocuğunuzun gelişimi hakkında sorularınızı yanıtlamak ve size yol göstermek için buradayım.\n\nSize nasıl yardımcı olabilirim?",
      time: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLimited, setIsLimited] = useState(!canSendMessage());
  const [remainingMessages, setRemainingMessages] = useState(
    MESSAGE_LIMIT - getMessageData().count
  );

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const canSend = canSendMessage();
    setIsLimited(!canSend);
    setRemainingMessages(MESSAGE_LIMIT - getMessageData().count);
  }, []);

  const sendToAPI = async (userMessage) => {
    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMessage }),
      });

      if (!response.ok) throw new Error("API error");

      const data = await response.json();
      return data.reply;
    } catch (error) {
      console.error("Chat API Error:", error);
      return "Şu anda bir teknik sorun yaşıyoruz. Lütfen daha sonra tekrar deneyin veya doğrudan bizimle iletişime geçin. 📞";
    }
  };

  const handleSend = async (text = input) => {
    const messageText = text.trim();
    if (!messageText || isLoading) return;

    if (!canSendMessage()) {
      setIsLimited(true);
      setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          text: "⏰ Bugünlük mesaj hakkınız doldu. Sorularınıza daha detaylı yanıt alabilmek için randevu almanızı öneriyorum. Yarın tekrar sohbet edebiliriz!",
          time: new Date(),
          showAppointment: true,
        },
      ]);
      return;
    }

    const userMessage = {
      type: "user",
      text: messageText,
      time: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    const newCount = incrementMessageCount();
    setRemainingMessages(MESSAGE_LIMIT - newCount);

    const botReply = await sendToAPI(messageText);

    setMessages((prev) => [
      ...prev,
      {
        type: "bot",
        text: botReply,
        time: new Date(),
        showAppointment: newCount >= MESSAGE_LIMIT - 1,
      },
    ]);

    setIsLoading(false);

    if (newCount >= MESSAGE_LIMIT) {
      setIsLimited(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-gradient-to-br from-teal-50 via-white to-amber-50 overflow-hidden">
      {/* Decorative Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-5 w-48 h-48 md:w-72 md:h-72 bg-teal-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-5 w-56 h-56 md:w-96 md:h-96 bg-amber-200/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 md:w-[500px] md:h-[500px] bg-rose-100/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative bg-white/90 backdrop-blur-md border-b border-teal-100 z-40 flex-shrink-0">
        <div className="w-full px-4 md:px-8 py-4 md:py-5">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div className="flex items-center gap-3 md:gap-5">
              <a
                href="/"
                className="flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors"
              >
                <IoArrowBack className="text-xl md:text-2xl" />
                <span className="text-sm md:text-base font-medium hidden sm:inline">
                  Ana Sayfa
                </span>
              </a>
              <div className="w-px h-10 bg-teal-200 hidden sm:block" />
              <div className="flex items-center gap-3 md:gap-4">
                <div className="relative">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg shadow-teal-200">
                    <RiRobot2Fill className="text-white text-2xl md:text-3xl" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-green-400 rounded-full border-2 border-white" />
                </div>
                <div>
                  <h1 className="font-bold text-gray-800 text-lg md:text-2xl">
                    Gelişim Asistanı
                  </h1>
                  <p className="text-sm md:text-base text-teal-600 flex items-center gap-1">
                    <IoSparkles className="text-sm md:text-base" />
                    Yapay Zeka Destekli
                  </p>
                </div>
              </div>
            </div>

            <a
              href="/iletisim"
              className="hidden md:flex items-center gap-2 bg-gradient-to-r from-amber-400 to-amber-500 
                         text-white px-6 py-3 rounded-xl font-semibold text-base shadow-lg shadow-amber-200 
                         hover:shadow-xl hover:shadow-amber-300 hover:-translate-y-0.5 transition-all"
            >
              <IoCalendar className="text-xl" />
              Randevu Al
            </a>
          </div>
        </div>
      </header>

      {/* Main Chat Area - Fills remaining space */}
      <main className="relative flex-1 flex flex-col min-h-0 w-full max-w-6xl mx-auto px-3 md:px-8 py-4 md:py-6">
        <div className="flex-1 flex flex-col min-h-0 bg-white/80 backdrop-blur-sm rounded-2xl md:rounded-3xl shadow-xl shadow-teal-100/50 border border-white overflow-hidden">
          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-5 md:space-y-8">
            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} />
            ))}

            {isLoading && (
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0">
                  <RiRobot2Fill className="text-white text-xl md:text-2xl" />
                </div>
                <div className="bg-gray-100 rounded-2xl rounded-tl-md px-5 py-4 md:px-6 md:py-5">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 md:w-3 md:h-3 bg-teal-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    />
                    <div
                      className="w-2.5 h-2.5 md:w-3 md:h-3 bg-teal-400 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    />
                    <div
                      className="w-2.5 h-2.5 md:w-3 md:h-3 bg-teal-400 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && !isLimited && (
            <div className="px-4 md:px-8 pb-4 flex-shrink-0">
              <p className="text-sm md:text-base text-gray-500 mb-3">
                Sık sorulan konular:
              </p>
              <div className="flex flex-wrap gap-2 md:gap-3">
                {quickQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSend(q.text)}
                    disabled={isLoading}
                    className="flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 
                               px-3 py-2 md:px-5 md:py-3 rounded-full text-sm md:text-base font-medium transition-all 
                               hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{q.icon}</span>
                    <span>{q.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="border-t border-gray-100 bg-gray-50/80 p-4 md:p-6 flex-shrink-0">
            {isLimited ? (
              <LimitReachedMessage />
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 mb-3 md:mb-4">
                  <IoTime className="text-gray-400 text-base md:text-lg" />
                  <span className="text-sm md:text-base text-gray-500">
                    Bugün{" "}
                    <span className="font-bold text-teal-600">
                      {remainingMessages}
                    </span>{" "}
                    mesaj hakkınız kaldı
                  </span>
                </div>

                <div className="flex gap-3 md:gap-4">
                  <div className="flex-1 relative">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={isLoading}
                      placeholder="Çocuğunuzla ilgili sorunuzu yazın..."
                      className="w-full px-5 py-4 md:px-6 md:py-5 bg-white border-2 border-gray-200 rounded-2xl 
                                 text-base md:text-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-teal-400 
                                 focus:ring-4 focus:ring-teal-100 transition-all disabled:opacity-50"
                    />
                  </div>
                  <button
                    onClick={() => handleSend()}
                    disabled={!input.trim() || isLoading}
                    className="px-5 md:px-7 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-2xl
                               hover:from-teal-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed
                               shadow-lg shadow-teal-200 hover:shadow-xl hover:shadow-teal-300 
                               transition-all hover:-translate-y-0.5 active:translate-y-0"
                  >
                    <IoSend className="text-2xl md:text-3xl" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-xs md:text-sm text-gray-400 mt-3 md:mt-4 px-4 hidden md:block">
          Bu asistan genel bilgi amaçlıdır ve profesyonel danışmanlık yerine
          geçmez.
        </p>
      </main>

      {/* Mobile Appointment Button */}
      <div className="md:hidden flex-shrink-0 p-3 bg-white/90 border-t border-gray-100">
        <a
          href="https://wa.me/+905055517747"
          className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-amber-400 to-amber-500 
                     text-white py-4 rounded-2xl font-bold text-base shadow-lg shadow-amber-200"
        >
          <IoCall className="text-xl" />
          Hemen Randevu Al
        </a>
      </div>
    </div>
  );
}

// ============================================
// MESSAGE BUBBLE COMPONENT
// ============================================
function MessageBubble({ message }) {
  const isBot = message.type === "bot";

  return (
    <div
      className={`flex items-start gap-3 md:gap-4 ${
        isBot ? "" : "flex-row-reverse"
      }`}
    >
      {/* Avatar */}
      <div
        className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0 ${
          isBot
            ? "bg-gradient-to-br from-teal-400 to-teal-600"
            : "bg-gradient-to-br from-amber-400 to-amber-500"
        }`}
      >
        {isBot ? (
          <RiRobot2Fill className="text-white text-xl md:text-2xl" />
        ) : (
          <RiUser3Fill className="text-white text-xl md:text-2xl" />
        )}
      </div>

      {/* Message Content */}
      <div
        className={`max-w-[85%] md:max-w-[75%] ${isBot ? "" : "text-right"}`}
      >
        <div
          className={`px-4 py-3 md:px-6 md:py-4 rounded-2xl ${
            isBot
              ? "bg-white shadow-md shadow-gray-100 rounded-tl-md text-gray-700"
              : "bg-gradient-to-r from-teal-500 to-teal-600 rounded-tr-md text-white"
          }`}
        >
          <p className="text-base md:text-lg leading-relaxed whitespace-pre-wrap">
            {message.text}
          </p>
        </div>

        {/* Time */}
        <p
          className={`text-xs md:text-sm text-gray-400 mt-1.5 md:mt-2 ${
            isBot ? "ml-2" : "mr-2"
          }`}
        >
          {message.time?.toLocaleTimeString("tr-TR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>

        {/* Appointment Button */}
        {isBot && (
          <div className="mt-3 md:mt-4">
            <a
              href="https://wa.me/+905055517747"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-amber-500 
                         text-white px-5 py-3 md:px-6 md:py-3.5 rounded-xl text-sm md:text-base font-semibold shadow-lg shadow-amber-200 
                         hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              <IoCalendar className="text-lg md:text-xl" />
              Randevu Al
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// LIMIT REACHED COMPONENT
// ============================================
function LimitReachedMessage() {
  return (
    <div className="text-center py-4 md:py-6">
      <div className="w-14 h-14 md:w-20 md:h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
        <IoTime className="text-amber-500 text-2xl md:text-4xl" />
      </div>
      <h3 className="font-bold text-gray-800 text-base md:text-xl mb-2">
        Günlük Mesaj Limitine Ulaştınız
      </h3>
      <p className="text-sm md:text-base text-gray-500 mb-4 md:mb-6 max-w-md mx-auto px-4">
        Sorularınıza daha detaylı yanıt alabilmek için uzmanımızla görüşmenizi
        öneriyoruz.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <a
          href="https://wa.me/+905055517747"
          className="flex items-center gap-2 bg-gradient-to-r from-teal-500 to-teal-600 
                     text-white px-6 py-3 md:px-8 md:py-4 rounded-xl font-semibold text-base md:text-lg shadow-lg shadow-teal-200 
                     hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          <IoCalendar className="text-xl md:text-2xl" />
          Randevu Al
        </a>
        <a
          href="tel:+905551234567"
          className="flex items-center gap-2 bg-white border-2 border-gray-200 
                     text-gray-700 px-6 py-3 md:px-8 md:py-4 rounded-xl font-semibold text-base md:text-lg
                     hover:border-teal-300 hover:bg-teal-50 transition-all"
        >
          <IoCall className="text-xl md:text-2xl" />
          Hemen Ara
        </a>
      </div>
    </div>
  );
}
